import { createElement } from '../../core/dom/helpers.js';

/** @type {Record<string, string>} */
const ICONS = { default: '\u25A3', material: '\u25C6' };
/** @type {Record<string, string>} */
const DISPLAY_NAMES = { default: 'Default', material: 'Material' };

const FALLBACK_ICON = '\u25A1';

let nextId = 0;

/**
 * Design selector dropdown. Trigger icon opens a menu with all available
 * designs; clicking one fires `onSelect`. Displays the current design name
 * next to a check mark.
 *
 * If only one design is available, no DOM is created.
 *
 * @param {HTMLElement} container
 * @param {{ designs: string[], current: string, onSelect: (next: string) => void }} opts
 * @returns {{ update(design: string): void, destroy(): void }}
 */
export function createDesignToggle(container, { designs, current, onSelect }) {
  if (designs.length <= 1) {
    return { update() {}, destroy() {} };
  }

  const dropdownId = `design-menu-${nextId++}`;
  let cur = current;
  let isOpen = false;
  /** @type {number} */
  let focusoutRafId = 0;

  /** @param {string} d */
  function iconFor(d) {
    return ICONS[d] || FALLBACK_ICON;
  }

  /** @param {string} d */
  function nameFor(d) {
    return DISPLAY_NAMES[d] || d.charAt(0).toUpperCase() + d.slice(1);
  }

  // Build trigger button
  const trigger = createElement('button', {
    type: 'button',
    class: 'design-toggle-trigger',
    'aria-haspopup': 'true',
    'aria-expanded': 'false',
    'aria-controls': dropdownId,
    'aria-label': `Design: ${nameFor(cur)}`,
  }, [iconFor(cur)]);

  // Build dropdown
  const dropdown = createElement('div', {
    class: 'design-toggle-dropdown',
    id: dropdownId,
    role: 'listbox',
    'aria-label': 'Select design',
  });

  /** @type {Map<string, HTMLElement>} */
  const itemMap = new Map();

  for (const d of designs) {
    const check = createElement('span', { class: 'design-toggle-check', 'aria-hidden': 'true' }, [d === cur ? '\u2713' : '']);
    const label = createElement('span', { class: 'design-toggle-label' }, [nameFor(d)]);
    const item = createElement('button', {
      type: 'button',
      class: 'design-toggle-item' + (d === cur ? ' design-toggle-item--active' : ''),
      role: 'option',
      'aria-selected': d === cur ? 'true' : 'false',
      'data-design': d,
    });
    item.appendChild(check);
    item.appendChild(label);
    dropdown.appendChild(item);
    itemMap.set(d, item);
  }

  // Wrapper
  const wrapper = createElement('div', { class: 'design-toggle' });
  wrapper.appendChild(trigger);
  wrapper.appendChild(dropdown);
  container.appendChild(wrapper);

  // Open / close
  /** @param {PointerEvent} e */
  function onDocPointerdown(e) {
    if (!wrapper.contains(/** @type {Node} */ (e.target))) {
      close();
    }
  }

  /** @param {KeyboardEvent} e */
  function onDocKeydown(e) {
    if (e.key === 'Escape') {
      close(true);
    }
  }

  function open() {
    if (isOpen) return;
    isOpen = true;
    wrapper.classList.add('design-toggle--open');
    trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', onDocPointerdown, { capture: true });
    document.addEventListener('keydown', onDocKeydown);
  }

  /** @param {boolean} [returnFocus] */
  function close(returnFocus) {
    if (!isOpen) return;
    isOpen = false;
    wrapper.classList.remove('design-toggle--open');
    trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', onDocPointerdown, { capture: true });
    document.removeEventListener('keydown', onDocKeydown);
    cancelAnimationFrame(focusoutRafId);
    if (returnFocus && trigger.isConnected) {
      trigger.focus();
    }
  }

  // Trigger click
  function onTriggerClick() {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }
  trigger.addEventListener('click', onTriggerClick);

  // Focusout
  function onFocusout() {
    if (!isOpen) return;
    focusoutRafId = requestAnimationFrame(() => {
      if (!isOpen) return;
      if (!wrapper.contains(document.activeElement)) {
        close();
      }
    });
  }
  wrapper.addEventListener('focusout', onFocusout);

  // Item clicks (delegated on wrapper)
  /** @param {MouseEvent} e */
  function onItemClick(e) {
    const target = /** @type {Element} */ (e.target);
    const itemEl = target.closest('[data-design]');
    if (!itemEl) return;
    const design = itemEl.getAttribute('data-design');
    if (!design) return;
    close();
    if (design !== cur) {
      onSelect(design);
    }
  }
  wrapper.addEventListener('click', onItemClick);

  /** @param {string} design */
  function update(design) {
    cur = design;
    // Update trigger
    trigger.textContent = iconFor(design);
    trigger.setAttribute('aria-label', `Design: ${nameFor(design)}`);
    // Update items
    for (const [d, item] of itemMap) {
      const isActive = d === design;
      item.classList.toggle('design-toggle-item--active', isActive);
      item.setAttribute('aria-selected', isActive ? 'true' : 'false');
      const check = item.querySelector('.design-toggle-check');
      if (check) check.textContent = isActive ? '\u2713' : '';
    }
  }

  function destroy() {
    close();
    trigger.removeEventListener('click', onTriggerClick);
    wrapper.removeEventListener('focusout', onFocusout);
    wrapper.removeEventListener('click', onItemClick);
    wrapper.remove();
  }

  return { update, destroy };
}
